import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import LanguageSelector from "./LanguageSelector";
import { isFeatureEnabled } from "../config/featureFlags";
import { localizePath } from "./LangRoute";

// Sprint 47 — activePaths del hub Professionals depèn del feature flag de
// manteniment. Quan està OFF, /mantenimiento-piscina no compta com a path
// actiu (a més de no estar enllaçat). Es manté la ruta tècnica al codi
// però invisible per al públic.
const PROFESSIONALS_ACTIVE_PATHS = [
  "/profesionales",
  "/instaladores",
  "/documentar-piscina",
  ...(isFeatureEnabled("maintenanceServiceVisible") ? ["/mantenimiento-piscina"] : []),
];

const NAV_ITEMS = [
  { labelKey: "header.passport", path: "/ficha-tecnica", activePaths: ["/ficha-tecnica", "/passport-generator"] },
  // Check passa a ser el HUB de comprovació de normativa: el test, els
  // índexs ICP/ICM i, des del 27/10/2026, les pàgines de normativa
  // municipal —urbanística i d'aigua—. Han de marcar «Check» com a
  // actiu o el visitant no sap d'on ha sortit, i «Calculadoras» no és
  // el seu lloc: allà es calcula, aquí es comprova què t'exigiran.
  {
    labelKey: "header.check",
    path: "/pool-check",
    activePaths: [
      "/pool-check", "/calculadora-icp", "/calculadora-icm",
      "/condiciones-piscina", "/normativa-agua-piscinas",
    ],
  },
  { labelKey: "header.calculators", path: "/calculadoras", activePaths: ["/calculadoras", "/calculadora-volumen-piscina", "/calculadora-consumo-electrico-piscina", "/tools", "/tools/pool-volume-calculator", "/tools/pool-energy-calculator", "/tools/pool-water-calculator"] },
  { labelKey: "header.professionals", path: "/profesionales", activePaths: PROFESSIONALS_ACTIVE_PATHS },
  // Vertical B2G "Contratación Pública": entrada visible al header (accés
  // directe a /contratacion-publica, que ja inclou el monitor demo embegut).
  // Nota: el feature flag `b2gVerticalVisible` NO es toca — segueix controlant
  // les seccions B2G de la Landing V3 (home), que resten ocultes per decisió
  // estratègica; aquí només s'exposa l'accés des del menú.
  { labelKey: "header.contratacion", path: "/contratacion-publica", activePaths: ["/contratacion-publica", "/contratacion-publica/monitor"] },
  { labelKey: "header.initiative", path: "/initiative", activePaths: ["/initiative"] },
  // Sprint 148 — El blog no era al menú. Són 16 articles en fins a sis
  // idiomes, la part del lloc que porta trànsit de cerca, i només s'hi
  // arribava per enllaç directe o des del peu.
  { labelKey: "header.blog", path: "/blog", activePaths: ["/blog"] },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Sprint 148 — El menú anava sempre a la ruta castellana. Des de /nl,
  // clicar "Calculadoras" et deixava a /calculadoras i, com que el
  // detector d'idioma només mira la URL, et canviava a castellà pel camí.
  // localizePath només prefixa el que existeix prefixat (PREFIXED_ROUTES),
  // així que /profesionales i companyia segueixen sense prefix, que és el
  // correcte.
  const go = (path) => navigate(localizePath(path, i18n.language));

  // El path pot venir prefixat (/nl/calculadoras): es treu el prefix abans
  // de comparar, o el subratllat d'actiu no s'encenia mai fora del castellà.
  const isActive = (item) => {
    const net = location.pathname.replace(/^\/(es|en|fr|de|it|nl)(?=\/|$)/, "") || "/";
    return item.activePaths.includes(net);
  };

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-14 gap-8">

          {/* Logo — Sprint 119.107 a11y: aria-label explícit + width/height */}
          <button
            onClick={() => navigate("/")}
            className="shrink-0 flex items-center"
            data-testid="nav-logo"
            aria-label={t("header.homeLink", "PoolLex.io — Home")}
          >
            <img src="/poollex-logo.png" alt="PoolLex.io — Clasificación digital sostenibilidad piscinas EN 17645" width="140" height="28" className="h-7 w-auto" />
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 flex-1" data-testid="site-nav">
            {NAV_ITEMS.map((item) => item.external ? (
              <a
                key={item.path}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-external-${item.labelKey.split(".").pop()}`}
                className="relative px-4 py-2 text-sm font-medium text-slate-500 hover:text-blue-700 transition-colors rounded-none inline-flex items-center gap-1.5"
              >
                {t(item.labelKey)}
                <span aria-hidden="true" className="text-[10px] text-slate-400">↗</span>
              </a>
            ) : (
              <button
                key={item.path}
                onClick={() => go(item.path)}
                data-testid={`nav-${item.path.replace("/", "").replace("-", "_")}`}
                className={`
                  relative px-4 py-2 text-sm font-medium transition-colors rounded-none
                  ${isActive(item)
                    ? "text-slate-900 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-slate-900"
                    : "text-slate-500 hover:text-slate-800"
                  }
                `}
              >
                {t(item.labelKey)}
              </button>
            ))}
          </nav>

          {/* Right: language + mobile toggle */}
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden md:block">
              <LanguageSelector />
            </div>

            {/* Mobile hamburger — Sprint 119.107 a11y: aria-label + aria-expanded */}
            <button
              className="md:hidden p-1 text-slate-500 hover:text-slate-900"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label={mobileOpen
                ? t("header.closeMobileMenu", "Cerrar menú")
                : t("header.openMobileMenu", "Abrir menú")}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu-panel"
            >
              {mobileOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile menu — Sprint 119.107 id per aria-controls */}
      {mobileOpen && (
        <div id="mobile-menu-panel" className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1" data-testid="mobile-menu">
          {NAV_ITEMS.map((item) => item.external ? (
            <a
              key={item.path}
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileOpen(false)}
              className="block w-full text-left px-3 py-2.5 text-sm font-medium rounded-sm text-blue-700 hover:bg-blue-50 transition-colors"
            >
              {t(item.labelKey)} <span aria-hidden="true" className="text-xs text-slate-400 ml-1">↗</span>
            </a>
          ) : (
            <button
              key={item.path}
              onClick={() => { go(item.path); setMobileOpen(false); }}
              className={`block w-full text-left px-3 py-2.5 text-sm font-medium rounded-sm transition-colors
                ${isActive(item) ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            >
              {t(item.labelKey)}
            </button>
          ))}
          <div className="pt-2 border-t border-slate-100">
            <LanguageSelector />
          </div>
        </div>
      )}
    </header>
  );
}
